import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicRoomSystem,
  RoomSystemVisibility,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { roomSystemQueryKey } from "../api/get-room-system";
import { useUpdateRoomSystemAsAdmin } from "../api/update-room-system-as-admin";
import {
  SessionRoomSystemFormData,
  sessionRoomSystemSchema,
} from "../types/session-room-system-schema";

type SessionRoomSystemFormProps = {
  initialData: PublicRoomSystem;
};

function apiDataToFormData({
  data,
  organisationId,
}: {
  data?: PublicRoomSystem;
  organisationId: string;
}): SessionRoomSystemFormData {
  if (!data) {
    return {
      organisationId: organisationId ?? "",
      displayName: "",
      email: "",
      visibility: RoomSystemVisibility.PRIVATE,
    };
  }

  return {
    organisationId: data.organisation.id,
    displayName: data.displayName,
    email: data.email,
    visibility: data.visibility,
  };
}

export function SessionRoomSystemForm(props: SessionRoomSystemFormProps) {
  const { initialData } = props;

  const { organisation } = useOrganisation();
  const form = useForm<SessionRoomSystemFormData>({
    resolver: zodResolver(sessionRoomSystemSchema),
    defaultValues: apiDataToFormData({
      data: initialData,
      organisationId: organisation.id,
    }),
  });

  const { mutateAsync: updateRoomSystem } = useUpdateRoomSystemAsAdmin({
    onSuccess: (roomSystem) => {
      form.reset(
        apiDataToFormData({
          data: roomSystem,
          organisationId: roomSystem.organisation.id,
        }),
      );

      queryClient.setQueryData(roomSystemQueryKey(roomSystem.id), roomSystem);
    },
  });

  async function handleUpdateRoomSystem(data: SessionRoomSystemFormData) {
    const { organisationId, ...roomSystemData } = data;
    try {
      await updateRoomSystem({
        id: initialData?.id ?? "",
        organisationId: organisationId,
        roomSystem: roomSystemData,
      });
    } catch (error) {
      const httpError = error as HttpError;

      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateRoomSystem)}>
        <Card>
          <Card.Header title="Room System Details" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Display Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    How your room system is displayed to users.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem className="space-y-3 rounded-md border border-border px-3 py-2">
                  <FormLabel>Privacy Level</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="PRIVATE" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Private
                          <span className="block text-sm text-muted-foreground">
                            Available to your organisation and group only
                          </span>
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="PUBLIC" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          Public
                          <span className="block text-sm text-muted-foreground">
                            Available to the entire ClassView network
                          </span>
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Email</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormDescription>
                    Notifications for this Room System will be sent to this
                    email address
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex justify-end">
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update Room System
              </Button>
            </Card.Footer>
          </motion.div>
        </Card>
      </form>
    </Form>
  );
}
